import { useState } from 'react';
import { Plus, Trash2, Clock } from 'lucide-react';
import type { TimeCondition, TimeRange } from '../../types/timeCondition';
import { WEEKDAYS } from '../../types/timeCondition';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheckbox,
  Button,
  Badge,
} from '../ui';

interface TimeConditionFormProps {
  condition: TimeCondition | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function TimeConditionForm({ condition, onSubmit, isLoading, onClose }: TimeConditionFormProps) {
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>(condition?.time_ranges || []);

  const addTimeRange = () => {
    setTimeRanges([...timeRanges, { weekday: 1, start_time: '08:00', end_time: '18:00' }]);
  };

  const removeTimeRange = (index: number) => {
    setTimeRanges(timeRanges.filter((_, i) => i !== index));
  };

  const updateTimeRange = (index: number, field: keyof TimeRange, value: any) => {
    const updatedRanges = [...timeRanges];
    updatedRanges[index] = {
      ...updatedRanges[index],
      [field]: value,
    };
    setTimeRanges(updatedRanges);
  };

  const getWeekdayLabel = (weekday: number) => {
    return WEEKDAYS.find(day => day.value === weekday)?.label || 'Desconhecido';
  };

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="description" required>
          Descrição
        </FormLabel>
        <FormInput
          id="description"
          name="description"
          defaultValue={condition?.description}
          required
        />
      </FormField>

      <FormField>
        <div className="flex items-center">
          <FormCheckbox id="active" name="active" defaultChecked={condition?.active ?? true} />
          <FormLabel htmlFor="active" className="ml-2 mb-0">
            Ativo
          </FormLabel>
        </div>
      </FormField>

      {/* Time Ranges Section */}
      <div className="mt-6 mb-4">
        <div className="flex justify-between items-center mb-2">
          <FormLabel className="text-lg flex items-center">
            <Clock className="h-5 w-5 mr-2" />
            Horários de Funcionamento
          </FormLabel>
          <Button type="button" variant="secondary" size="sm" onClick={addTimeRange}>
            <Plus className="h-4 w-4 mr-1" /> Adicionar Horário
          </Button>
        </div>

        {timeRanges.length === 0 ? (
          <div className="text-center py-4 text-gray-500 border border-dashed rounded-md">
            Nenhum horário adicionado
          </div>
        ) : (
          <div className="space-y-3">
            {timeRanges.map((range, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-md"
              >
                {/* Hidden input for time range ID */}
                {range.id && (
                  <input type="hidden" name={`time_ranges[${index}].id`} value={range.id} />
                )}

                <div className="flex-1">
                  <FormField>
                    <FormLabel htmlFor={`time_ranges[${index}].weekday`} required>
                      Dia da Semana
                    </FormLabel>
                    <FormSelect
                      id={`time_ranges[${index}].weekday`}
                      name={`time_ranges[${index}].weekday`}
                      value={range.weekday}
                      onChange={e => updateTimeRange(index, 'weekday', parseInt(e.target.value))}
                      required
                    >
                      {WEEKDAYS.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </FormSelect>
                  </FormField>
                </div>

                <div className="w-32">
                  <FormField>
                    <FormLabel htmlFor={`time_ranges[${index}].start_time`} required>
                      Início
                    </FormLabel>
                    <FormInput
                      type="time"
                      id={`time_ranges[${index}].start_time`}
                      name={`time_ranges[${index}].start_time`}
                      value={range.start_time}
                      onChange={e => updateTimeRange(index, 'start_time', e.target.value)}
                      required
                    />
                  </FormField>
                </div>

                <div className="w-32">
                  <FormField>
                    <FormLabel htmlFor={`time_ranges[${index}].end_time`} required>
                      Fim
                    </FormLabel>
                    <FormInput
                      type="time"
                      id={`time_ranges[${index}].end_time`}
                      name={`time_ranges[${index}].end_time`}
                      value={range.end_time}
                      onChange={e => updateTimeRange(index, 'end_time', e.target.value)}
                      required
                    />
                  </FormField>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-8"
                  onClick={() => removeTimeRange(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Section */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <h3 className="text-sm font-medium mb-2">Resumo da Configuração</h3>

        {timeRanges.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Horários de Funcionamento:</p>
            <div className="flex flex-wrap gap-2">
              {timeRanges.map((range, index) => (
                <Badge key={index} variant="secondary">
                  {getWeekdayLabel(range.weekday)}: {range.start_time} - {range.end_time}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {condition ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default TimeConditionForm;
